import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { authService } from '../services/authService';
import { logout, setUser } from '../store/slices/authSlice';
import type { RootState } from '../store';
import { persistor } from '../store';
import type { User } from '../types';
import { FieldError, inputErrorClass } from '../components/FieldError';

export function ProfilePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const current = useSelector((s: RootState) => s.auth.user);
  const [form, setForm] = useState<User | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (current) {
      setForm({
        id: current.id,
        name: current.name,
        email: current.email,
        role: current.role,
        plan: current.plan,
        company: current.company ?? '',
        designation: current.designation ?? '',
        mobile: current.mobile ?? '',
        birthday: current.birthday ?? '',
        avatar: current.avatar ?? '',
      });
    }
  }, [current]);

  const pickAvatar = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const r = reader.result as string;
        setForm((f) => (f ? { ...f, avatar: r } : f));
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const save = async () => {
    if (!form) return;
    setErrors({});
    if (!form.name.trim()) {
      setErrors({ name: 'Full name is required.' });
      return;
    }
    setSaving(true);
    try {
      await api.put('/api/users/me', {
        name: form.name,
        company: form.company,
        designation: form.designation,
        mobile: form.mobile,
        birthday: form.birthday,
        avatar: form.avatar,
      });
      dispatch(setUser(form));
      window.alert('Profile updated successfully.');
    } catch (e: unknown) {
      setErrors({ submit: e instanceof Error ? e.message : 'Failed to update' });
    } finally {
      setSaving(false);
    }
  };

  const signOut = async () => {
    if (!window.confirm('Sign out?')) return;
    await authService.signOut();
    dispatch(logout());
    await persistor.purge();
    queryClient.clear();
    navigate('/');
  };

  if (!form) return <p className="text-slate-500">Loading...</p>;

  return (
    <div>
      <h1 className="text-xl font-black text-white bg-blue-600 -mx-4 px-4 py-4 mb-6 rounded-b-2xl">Profile</h1>

      <div className="flex flex-col items-center mb-8">
        <div className="relative">
          {form.avatar ? (
            <img src={form.avatar} alt="" className="w-28 h-28 rounded-full border-4 border-blue-100 object-cover" />
          ) : (
            <div className="w-28 h-28 rounded-full border-4 border-blue-100 bg-blue-100 flex items-center justify-center text-4xl font-bold text-blue-600">
              {form.name.charAt(0).toUpperCase()}
            </div>
          )}
          <button
            type="button"
            onClick={pickAvatar}
            className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-blue-600 text-white border-4 border-white text-xs"
          >
            +
          </button>
        </div>
        <p className="text-[10px] font-black text-blue-600 uppercase mt-2">Change picture</p>
      </div>

      <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Full name</label>
      <input
        className={`w-full rounded-2xl bg-slate-100 px-4 py-3 mb-1 ${errors.name ? inputErrorClass : ''}`}
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
      />
      <FieldError message={errors.name} />

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Designation</label>
          <input
            className="w-full rounded-2xl bg-slate-100 px-3 py-3 text-sm"
            value={form.designation}
            onChange={(e) => setForm({ ...form, designation: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Plan</label>
          <div className="rounded-2xl bg-slate-100 px-3 py-3 text-sm font-bold text-blue-600 uppercase">{form.plan}</div>
        </div>
      </div>

      <label className="block text-[10px] font-black text-slate-400 uppercase mb-1 mt-4">Company</label>
      <input
        className="w-full rounded-2xl bg-slate-100 px-4 py-3"
        value={form.company}
        onChange={(e) => setForm({ ...form, company: e.target.value })}
      />

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Birthday</label>
          <input
            type="date"
            className="w-full rounded-2xl bg-slate-100 px-3 py-3 text-sm"
            value={form.birthday ? form.birthday.slice(0, 10) : ''}
            onChange={(e) => setForm({ ...form, birthday: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Mobile</label>
          <input
            className="w-full rounded-2xl bg-slate-100 px-3 py-3 text-sm"
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
          />
        </div>
      </div>

      <label className="block text-[10px] font-black text-slate-400 uppercase mb-1 mt-4">Email</label>
      <div className="rounded-2xl bg-slate-50 px-4 py-3 text-slate-500 text-sm font-semibold">{form.email}</div>

      <FieldError message={errors.submit} />
      <button
        type="button"
        disabled={saving}
        onClick={save}
        className="w-full py-4 rounded-2xl bg-blue-600 text-white font-black mt-8 disabled:opacity-50"
      >
        {saving ? '...' : 'Save profile'}
      </button>

      <button
        type="button"
        onClick={signOut}
        className="w-full py-4 mt-4 rounded-2xl border border-red-100 text-red-600 font-bold"
      >
        Sign out
      </button>
    </div>
  );
}
